// ProgramFlow.tsx — AI-drafted training programs (PROGRAM-DESIGN.md).
// Renders the Home-screen state machine: no active program -> "Draft a program" CTA (prompting a
// one-time goals setup first if needed) -> drafting -> review/edit the AI's proposal -> Accept
// (writes real programs+program_sessions) or Discard (nothing saved) -> once accepted, the NEXT UP
// hero shows the next pending session with a Start button, same pre-load shape as today's Routines.
import { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, ChevronRight, X, Trash2, RefreshCw, AlertTriangle } from "lucide-react";
import { store, type TrainingProfile, type TrainingGoal, type Program, type ProgramSession, type ProgramExercise, type LiftSignal, type ExercisePR } from "./store";
import { draftProgram, BrainError, type DraftProgramResult, type DraftProgramWeek } from "./brain";
import { presetExercises, muscleGroups } from "./exercises";
import { ExerciseIcon } from "./exerciseVisual";
import { formatWeight } from "./policy";

function ModalShell({ title, onCancel, children }: { title: string; onCancel: () => void; children: React.ReactNode }) {
  // 2026-09-03 — Apple 4 Design on iPad: "the profile page was not fully visible". A fixed, centred overlay
    // does not scroll when the on-screen keyboard shrinks the visual viewport (the text field below focuses the
    // keyboard), so the Save button sat under it. The overlay itself now scrolls and the card is top-aligned,
    // so every control is reachable with the keyboard up, in any orientation.
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "start center", zIndex: 50, padding: "max(1rem, env(safe-area-inset-top)) 1rem max(1rem, env(safe-area-inset-bottom))", overflowY: "auto", WebkitOverflowScrolling: "touch" as any }}>
      <div className="card" style={{ width: "100%", maxWidth: 560, maxHeight: "none", marginTop: "min(8vh, 48px)", marginBottom: "40vh", padding: "1.25rem" }}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "1rem" }}>
          <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>{title}</div>
          <button className="btn" style={{ padding: "0.3rem" }} onClick={onCancel} aria-label="Close"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Step 1: one-time goals setup (editable anytime) ─────────────────────────────
function ProfileSetup({ initial, onSaved, onCancel }: { initial: TrainingProfile | null; onSaved: (p: TrainingProfile) => void; onCancel: () => void }) {
  const [goal, setGoal] = useState<TrainingGoal>(initial?.goal ?? "strength");
  const [days, setDays] = useState(initial?.days_per_week ?? 3);
  const [avoid, setAvoid] = useState(initial?.avoid ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true); setErr(null);
    try { onSaved(await store.setTrainingProfile(goal, days, avoid.trim())); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <ModalShell title="Training goals" onCancel={onCancel}>
      <div style={{ display: "grid", gap: "0.9rem" }}>
        <div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: 4 }}>Goal</div>
          <div className="row" style={{ gap: "0.4rem" }}>
            {(["strength", "hypertrophy", "general_fitness"] as const).map((g) => (
              <button key={g} type="button" className={`btn${goal === g ? " btn-primary" : ""}`} style={{ fontSize: "var(--fs-sm)" }} onClick={() => setGoal(g)}>
                {g === "general_fitness" ? "General fitness" : g[0].toUpperCase() + g.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: 4 }}>Days per week</div>
          <div className="row" style={{ gap: "0.4rem" }}>
            {[2, 3, 4, 5, 6].map((d) => (
              <button key={d} type="button" className={`btn${days === d ? " btn-primary" : ""}`} style={{ fontSize: "var(--fs-sm)", width: 40 }} onClick={() => setDays(d)}>{d}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: 4 }}>Anything to avoid (optional)</div>
          <input value={avoid} onChange={(e) => setAvoid(e.target.value)} placeholder="e.g. no overhead pressing, bad shoulder" />
        </div>
        <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
        {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)" }}>{err}</div>}
      </div>
    </ModalShell>
  );
}

// ── Step 2: review + edit the AI's draft before anything is saved ──────────────
function ExerciseRow({ ex, onChange, onRemove }: { ex: ProgramExercise; onChange: (e: ProgramExercise) => void; onRemove: () => void }) {
  return (
    <div className="row" style={{ gap: "0.4rem", alignItems: "center", padding: "0.4rem 0", borderTop: "1px solid var(--border)", flexWrap: "wrap" }}>
      <ExerciseIcon name={ex.name} size={13} />
      <select value={ex.slug} onChange={(e) => {
        const p = presetExercises.find((x) => x.slug === e.target.value);
        onChange({ ...ex, slug: e.target.value, name: p?.name ?? e.target.value });
      }} style={{ flex: "2 1 160px", width: "auto", fontSize: "var(--fs-sm)" }}>
        {/* The AI can name a sensible complementary movement outside our ~900-exercise library
            (PROGRAM-DESIGN.md's draft_program rule) — an unmatched value falls back to the browser's
            first <option> (Bench Press), silently mislabeling a real drafted exercise. Keep it real
            and still editable to a preset via this synthetic option. */}
        {!presetExercises.some((p) => p.slug === ex.slug) && <option value={ex.slug}>{ex.name}</option>}
        {muscleGroups.map((g) => {
          const items = presetExercises.filter((p) => p.muscleGroup === g);
          if (!items.length) return null;
          return <optgroup key={g} label={g}>{items.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}</optgroup>;
        })}
      </select>
      <input type="number" min="1" value={ex.target_sets} onChange={(e) => onChange({ ...ex, target_sets: parseInt(e.target.value, 10) || 1 })}
        title="Sets" style={{ width: 52, fontSize: "var(--fs-sm)" }} />
      <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>×</span>
      <input type="number" min="1" value={ex.target_reps} onChange={(e) => onChange({ ...ex, target_reps: parseInt(e.target.value, 10) || 1 })}
        title="Reps" style={{ width: 52, fontSize: "var(--fs-sm)" }} />
      <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>@</span>
      <input type="number" step="0.5" min="0" value={ex.target_weight} onChange={(e) => onChange({ ...ex, target_weight: parseFloat(e.target.value) || 0 })}
        title="Weight" style={{ width: 70, fontSize: "var(--fs-sm)" }} />
      <button type="button" className="btn" style={{ padding: "0.25rem", marginLeft: "auto" }} onClick={onRemove} aria-label="Remove exercise"><Trash2 size={14} /></button>
    </div>
  );
}

function ProgramDraftReview({ draft, onAccept, onDiscard }: { draft: DraftProgramResult; onAccept: (weeks: DraftProgramWeek[], name: string) => void; onDiscard: () => void }) {
  const [weeks, setWeeks] = useState<DraftProgramWeek[]>(draft.weeks);

  function updateExercise(wi: number, si: number, ei: number, ex: ProgramExercise) {
    setWeeks((prev) => prev.map((w, iw) => iw !== wi ? w : {
      ...w, sessions: w.sessions.map((s, is) => is !== si ? s : { ...s, exercises: s.exercises.map((e, ie) => ie !== ei ? e : ex) }),
    }));
  }
  function removeExercise(wi: number, si: number, ei: number) {
    setWeeks((prev) => prev.map((w, iw) => iw !== wi ? w : {
      ...w, sessions: w.sessions.map((s, is) => is !== si ? s : { ...s, exercises: s.exercises.filter((_, ie) => ie !== ei) }),
    }));
  }

  return (
    <ModalShell title={draft.program_name} onCancel={onDiscard}>
      <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.5, marginTop: 0 }}>{draft.rationale}</p>
      {weeks.map((w) => (
        <div key={w.week_index} style={{ marginBottom: "1rem" }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", margin: "0.5rem 0" }}>Week {w.week_index + 1}</div>
          {w.sessions.map((s, si) => (
            <div key={s.day_index} className="card" style={{ padding: "0.7rem 0.9rem", marginBottom: "0.5rem" }}>
              <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{s.label}</div>
              {s.exercises.map((ex, ei) => (
                <ExerciseRow key={ei} ex={ex} onChange={(v) => updateExercise(w.week_index, si, ei, v)} onRemove={() => removeExercise(w.week_index, si, ei)} />
              ))}
            </div>
          ))}
        </div>
      ))}
      <div className="row" style={{ gap: "0.5rem", justifyContent: "flex-end", marginTop: "0.5rem" }}>
        <button className="btn" onClick={onDiscard}>Discard</button>
        <button className="btn btn-primary" onClick={() => onAccept(weeks, draft.program_name)}>Accept — start this program</button>
      </div>
    </ModalShell>
  );
}

// ── The Home-screen piece: NEXT UP hero, or the draft-a-program flow ───────────
export function ProgramSection({ onProgramChanged, onStartSession, refreshSignal, triggerDraft }: {
  onProgramChanged: () => void;
  onStartSession: (session: ProgramSession) => void;
  refreshSignal: number;
  triggerDraft?: number;
}) {
  const [active, setActive] = useState<Program | null>(null);
  const [next, setNext] = useState<ProgramSession | null>(null);
  const [showProfile, setShowProfile] = useState(false);
  const [draft, setDraft] = useState<DraftProgramResult | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const [a, n] = await Promise.all([store.activeProgram(), store.nextProgramSession()]);
    setActive(a); setNext(n);
  }, []);
  useEffect(() => { void reload(); }, [reload, refreshSignal]);
  // "Draft me a program" from chat (chat/brain.ts isDraftProgramRequest) — same entry point as
  // tapping the button below. Skips the initial mount so a stray 0 never auto-opens the flow.
  const draftTriggerSeen = useRef(triggerDraft ?? 0);
  useEffect(() => {
    if (triggerDraft !== undefined && triggerDraft !== draftTriggerSeen.current) {
      draftTriggerSeen.current = triggerDraft;
      void startDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerDraft]);

  async function startDraft() {
    setErr(null);
    const profile = await store.getTrainingProfile();
    if (!profile) { setShowProfile(true); return; }
    await runDraft(profile);
  }

  async function runDraft(profile: TrainingProfile) {
    setDrafting(true); setErr(null);
    try {
      const signals = await store.trendSignals(56); // ~8 weeks, matches PROGRAM-DESIGN.md input
      const prs: ExercisePR[] = await store.listPRs();
      const perLift: LiftSignal[] = signals.per_lift ?? [];
      const result = await draftProgram(profile, perLift, prs, 1);
      setDraft(result);
    } catch (e) {
      setErr(e instanceof BrainError ? e.message : "Couldn't draft a program — please try again.");
    } finally {
      setDrafting(false);
    }
  }

  async function accept(weeks: DraftProgramWeek[], name: string) {
    await store.createProgram({ name, goal: (await store.getTrainingProfile())?.goal ?? "general_fitness", weeks });
    setDraft(null);
    await reload();
    onProgramChanged();
  }

  if (showProfile) {
    return <ProfileSetup initial={null} onCancel={() => setShowProfile(false)} onSaved={(p) => { setShowProfile(false); void runDraft(p); }} />;
  }
  if (draft) {
    return <ProgramDraftReview draft={draft} onDiscard={() => setDraft(null)} onAccept={(weeks, name) => void accept(weeks, name)} />;
  }

  if (active && next) {
    return (
      <div className="card card-in" style={{
        padding: "1.1rem 1.25rem 1.25rem", marginBottom: "1rem", overflow: "hidden", position: "relative",
        background: "linear-gradient(155deg, color-mix(in srgb, var(--accent) 10%, var(--elev-1)), var(--elev-1) 65%)",
        border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--border))",
      }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div className="row" style={{ gap: "0.3rem", color: "var(--accent)", fontSize: "var(--fs-xs)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
              <Sparkles size={12} /> Next up
            </div>
            <div style={{ fontWeight: 800, fontSize: "1.5rem", margin: "0.15rem 0 0.7rem", letterSpacing: "-0.01em" }}>{next.label}</div>
          </div>
          <button className="btn btn-primary row" style={{ gap: "0.3rem", fontSize: "var(--fs-sm)", flexShrink: 0 }} onClick={() => onStartSession(next)}>
            Start <ChevronRight size={14} />
          </button>
        </div>
        <div style={{ display: "grid", gap: "0.4rem" }}>
          {next.exercises.map((e, i) => (
            <div key={i} className="row" style={{
              justifyContent: "space-between", gap: "0.75rem", padding: "0.5rem 0.7rem",
              background: "color-mix(in srgb, var(--elev-1) 60%, transparent)", borderRadius: 10,
              border: "1px solid color-mix(in srgb, var(--border) 70%, transparent)",
            }}>
              <div className="row" style={{ gap: "0.55rem", minWidth: 0 }}>
                <ExerciseIcon name={e.name} />
                <span style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{e.name}</span>
              </div>
              <span className="tnum" style={{ fontSize: "var(--fs-sm)", fontWeight: 700, flexShrink: 0 }}>
                {e.target_sets}×{e.target_reps}{e.target_weight > 0 ? ` @ ${formatWeight(e.target_weight)}` : ""}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{
      padding: "1.1rem 1.25rem", marginBottom: "1rem",
      background: "linear-gradient(155deg, color-mix(in srgb, var(--accent) 10%, var(--elev-1)), var(--elev-1) 65%)",
      border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--border))",
    }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <div className="row" style={{ gap: "0.4rem", color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)" }}><Sparkles size={14} /> Draft a program</div>
        <button className="btn btn-primary row" style={{ gap: "0.3rem", fontSize: "var(--fs-sm)" }} disabled={drafting} onClick={() => void startDraft()}>
          {drafting ? <><RefreshCw size={13} className="spin" /> Drafting…</> : <>Start <ChevronRight size={14} /></>}
        </button>
      </div>
      <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0.4rem 0 0", lineHeight: 1.45 }}>
        The AI designs a real week of training from your logged numbers and stated goals — you review and edit it before anything's saved.
      </p>
      {err && <div className="row" style={{ marginTop: 8, gap: "0.35rem", color: "var(--rose)", fontSize: "var(--fs-xs)" }}><AlertTriangle size={13} />{err}</div>}
    </div>
  );
}
